import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The payment element as the browser test draws it (plan-005 OP-37 Task 2, design §18.14.3): the same
 * card the website widget draws — the shop, the order's reference, the total, a Pay button opening
 * `/p/<code>` in a new tab and when the link expires — in the element's own words (the conversation's
 * language), marked test. A settled element says paid / expired / not completed instead of the button.
 *
 * It sits inside the transcript's polite `role="log"`, so its arrival and its state line are read out.
 * Opening the page is said in a status line. A state that removes the button while it has focus moves
 * focus to the card, never to nowhere.
 */
import { Tag } from "@wetalk/ui";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { formatMoney } from "../../money.js";
import { expiresWordsOf, paymentStateWordsOf, type PaymentElementView } from "./payment-element.js";

export function PaymentElementCard({ element }: { readonly element: PaymentElementView }): ReactNode {
  usePortalTranslation("operations");
  const [opened, setOpened] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  /**
   * Whether the button held focus last: set on its focus, cleared only when focus moves to another
   * element (a blur with a `relatedTarget`) — the blur a browser may fire as the button is removed has
   * none, so it does not clear it.
   */
  const payHadFocus = useRef(false);
  const settled = element.state !== null;

  useLayoutEffect(() => {
    const node = card.current;
    if (!settled || !payHadFocus.current || node === null) return;
    payHadFocus.current = false;
    const active = node.ownerDocument.activeElement;
    // Only when the removal left focus nowhere; focus the tester moved elsewhere stays where it is.
    if (active === null || active === node.ownerDocument.body) node.focus();
  }, [settled]);

  const stateWords = paymentStateWordsOf(element);
  return (
    <div
      ref={card}
      className="tc-payment"
      role="group"
      aria-label={element.text.element_title}
      tabIndex={-1}
      data-state={settled ? element.state : "payable"}
    >
      <div className="tc-payment-head">
        <span className="tc-payment-title">{element.text.element_title}</span>
        {element.isTest ? <Tag tone="neutral">{element.text.element_is_test_label}</Tag> : null}
      </div>
      <span className="tc-payment-detail">{element.shopName}</span>
      <span className="tc-payment-detail">
        {element.text.element_reference_label} {element.customerOrderReference}
      </span>
      <span className="tc-payment-amount">{formatMoney(element.amount)}</span>
      {stateWords === null ? (
        <>
          <a
            className="btn btn-primary tc-payment-pay"
            href={element.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setOpened(true);
            }}
            onFocus={() => {
              payHadFocus.current = true;
            }}
            onBlur={(event) => {
              if (event.relatedTarget !== null) payHadFocus.current = false;
            }}
          >
            {element.text.element_pay_label}
          </a>
          <span className="tc-payment-detail">{expiresWordsOf(element)}</span>
          {opened ? (
            <span className="tc-payment-detail" role="status">
              {portalText("operations", "the_payment_page_opened_in_a_new_tab_8d3b5497")}</span>
          ) : null}
        </>
      ) : (
        <span className="tc-payment-state">{stateWords}</span>
      )}
    </div>
  );
}
